// apps/client/src/components/common/QueryCacheLifecycle.tsx
// Mounts the dashboard cache-safety rules (see useQueryCacheLifecycle.ts)
// once, globally, inside AuthProvider + CompanyProvider. Renders nothing.
//
// It must live at the app level — NOT inside a dashboard page — because an
// upload can finish aggregating while the user is on the upload screen; the
// invalidation has to fire regardless of which page is mounted.
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../context/CompanyContext';
import { useAggregationStatus } from '../../hooks/useAggregationStatus';
import { useQueryCacheLifecycle } from '../../hooks/useQueryCacheLifecycle';
import { useDashboardCacheMode } from '../../hooks/useDashboardQuery';

// Roles with no dashboards, whose reads of the watched collections are denied by
// firestore.rules: the companies/{id} catch-all excludes isMarketing(), and
// hasGeneralAppAccess() excludes service_staff. Watching for them only logged
// "Missing or insufficient permissions" on every page (caught by the marketing
// RBAC smoke, 2026-10-13).
const ROLES_WITHOUT_DASHBOARDS = new Set(['marketing', 'service_staff']);

const QueryCacheLifecycle: React.FC = () => {
  const { user, claims } = useAuth();
  // company.id and the userData.companyId used by dashboard pages both derive
  // from users/{uid}.companyId (CompanyProvider reads the same doc), so the
  // aggregation watcher and the queries it resets always share a tenant.
  const { company } = useCompany();
  // No watch until the role claim is known, so a dashboard-less role never
  // starts the listener during sign-in.
  const canWatch = !!claims?.role && !ROLES_WITHOUT_DASHBOARDS.has(claims.role);
  const watchCompanyId = canWatch ? company?.id || '' : '';
  const isAggregating = useAggregationStatus(watchCompanyId);
  const cacheMode = useDashboardCacheMode();
  const client = useQueryClient();

  useQueryCacheLifecycle(client, user?.uid ?? null, isAggregating, cacheMode);

  return null;
};

export default QueryCacheLifecycle;
